import React from 'react';
import { useTranslation } from 'react-i18next';
import { GraphQLFormattedError } from 'graphql';
import { Nomenclature } from 'graphql-common';
import { FieldItem, Values } from '@lib/interfaces/form';
import Form, {
  ActionsProps,
} from '@lib/components/ReactHookForm/FormContainer';
import replaceNullsWithUndefined from '@lib/utils/replaceNullsWithUndefined';
import Modal from '@lib/components/Modal/Modal';
import { GetLoadOptions } from '@lib/components/Select/types';
import getDefaultNomenclatureFormValuesFromQueryData from 'components/AssetNomenclatureModalForm/utils/getDefaultNomenclatureFormValuesFromQueryData';
import { SubmitAction } from '@lib/enums/form';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import AssetNomenclatureModalFormActions from './AssetNomenclatureModalFormActions';
import getFormFields from './utils/getFormFields';

export interface FormProps {
  assetNomenclature?: Nomenclature;
  getAssetsLoadOptions: GetLoadOptions;
  graphQLErrors?: readonly GraphQLFormattedError[];
  isEditForm?: boolean;
  isOpen: boolean;
  loading: boolean;
  onCancel: () => void;
  onClose: () => void;
  onSubmit: (values: Values, isEditForm?: boolean) => void;
  submitNewItemRef: React.RefObject<SubmitAction | undefined>;
  formRef: FormRefType;
}

export default function AssetNomenclatureModalForm(props: FormProps) {
  const {
    assetNomenclature,
    getAssetsLoadOptions,
    graphQLErrors,
    isEditForm,
    isOpen,
    loading,
    onCancel,
    onClose,
    onSubmit,
    submitNewItemRef,
    formRef,
  } = props;
  const { t } = useTranslation();

  const fields: FieldItem[] = getFormFields({
    getAssetsLoadOptions,
    isEditForm,
    t,
  });

  const defaultValues = replaceNullsWithUndefined(
    getDefaultNomenclatureFormValuesFromQueryData(assetNomenclature),
  );

  const actionsComponent: (
    actionsComponentProps: ActionsProps,
  ) => React.ReactElement = (actionsComponentProps) => (
    <AssetNomenclatureModalFormActions
      {...actionsComponentProps}
      isEditForm={isEditForm}
      submitNewItemRef={submitNewItemRef}
      onCancel={onCancel}
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t(isEditForm ? 'edit-nomenclature' : 'add-nomenclature')}
      titleAlign="left"
    >
      <Form
        actions={actionsComponent}
        actionsComponentProps={{ onCancel }}
        defaultValues={defaultValues}
        fields={fields}
        formId="nomenclature"
        formRef={formRef}
        graphQLErrors={graphQLErrors}
        loading={loading}
        onSubmit={(values) => onSubmit(values, isEditForm)}
      />
    </Modal>
  );
}
